import React, { useEffect, useState } from "react";
import { Card, CardContent } from "../ui/card";
import { BillingService } from "../../services/BillingService";
import { usePermissions } from "../../PermissionsProvider";
import { Skeleton } from "../ui/skeleton";
import { BarChart3Icon } from "lucide-react";
import { Button } from "../ui/button";

interface AgentUsageData {
  date: string;
  requests: number;
  cost: number;
  prompt_tokens: number;
  completion_tokens: number;
}

const AgentUsage: React.FC = () => {
  const { apiKey } = usePermissions();
  const [usageData, setUsageData] = useState<AgentUsageData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<number>(30);

  useEffect(() => {
    if (apiKey) {
      fetchAgentUsage(selectedPeriod);
    }
  }, [apiKey, selectedPeriod]);

  const fetchAgentUsage = (days: number) => {
    setLoading(true);
    setError(null);

    BillingService.getAgentUsage(days, null, apiKey).subscribe(
      (result: AgentUsageData[]) => {
        console.log("Agent usage data:", result);
        setUsageData(result);
        setLoading(false);
      },
      (error: any) => {
        console.error("Error fetching agent usage:", error);
        setError(error.message || "Failed to load usage data");
        setLoading(false);
      }
    );
  };

  const totalSpend = usageData.reduce((sum, day) => sum + day.cost, 0);
  const totalRequests = usageData.reduce((sum, day) => sum + day.requests, 0);

  // Calculate max values for chart scaling
  const maxCost = Math.max(...usageData.map((d) => d.cost), 1);
  const maxRequests = Math.max(...usageData.map((d) => d.requests), 1);

  // Format date for display (e.g., "Oct 22")
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  // Format number with K/M suffix
  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-gc-demi-bold text-foreground">Usage</h2>
          <p className="text-sm text-muted-foreground mt-1 font-gc-reg">
            Monitor your agent usage and costs
          </p>
        </div>

        {/* Period Selector */}
        <div className="flex gap-2">
          <Button
            variant={selectedPeriod === 7 ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedPeriod(7)}
            className="font-gc-reg">
            7d
          </Button>
          <Button
            variant={selectedPeriod === 30 ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedPeriod(30)}
            className="font-gc-reg">
            30d
          </Button>
          <Button
            variant={selectedPeriod === 90 ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedPeriod(90)}
            className="font-gc-reg">
            90d
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-4">
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      ) : error ? (
        <Card>
          <CardContent className="p-8">
            <div className="text-center text-red-500 font-gc-reg">{error}</div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Total Spend Chart */}
          <Card className="border-border">
            <CardContent className="p-6">
              <div className="space-y-4">
                {/* Chart Header */}
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-sm text-muted-foreground font-gc-reg mb-1">
                      Total Spend
                    </h3>
                    <div className="text-3xl font-gc-demi-bold text-foreground">
                      {totalSpend.toLocaleString()} credits
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground font-gc-reg">
                    {selectedPeriod}d
                  </div>
                </div>

                {/* Bar Chart */}
                <div className="h-48 flex items-end gap-1 pt-4 pb-6 bg-background/50 rounded px-2 relative">
                  {usageData.length === 0 ? (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground font-gc-reg">
                      No usage data available
                    </div>
                  ) : (
                    usageData.map((day, index) => {
                      const heightPercent = (day.cost / maxCost) * 100;
                      const isFirstDay = index === 0;
                      const isLastDay = index === usageData.length - 1;
                      const showLabel =
                        usageData.length <= 10 ||
                        isFirstDay ||
                        isLastDay ||
                        (usageData.length > 10 &&
                          index % Math.ceil(usageData.length / 6) === 0);

                      return (
                        <div
                          key={day.date}
                          className="flex-1 flex items-end justify-center group relative min-w-[3px]">
                          {/* Bar */}
                          <div
                            className="w-full bg-maitai-green hover:bg-maitai-green/80 transition-colors rounded-t cursor-pointer"
                            style={{
                              height: `${Math.max(heightPercent, 3)}%`,
                              minHeight: day.cost > 0 ? "6px" : "2px",
                            }}
                          />

                          {/* Tooltip on hover */}
                          <div className="absolute bottom-full mb-2 hidden group-hover:block bg-popover border border-border rounded px-2 py-1 text-xs font-gc-reg whitespace-nowrap z-10 shadow-lg">
                            <div className="font-gc-med">
                              {formatDate(day.date)}
                            </div>
                            <div className="text-muted-foreground">
                              {day.cost.toLocaleString()} credits • {day.requests} requests
                            </div>
                          </div>

                          {/* Date Label */}
                          {showLabel && (
                            <div className="absolute top-full mt-1 text-[10px] text-muted-foreground font-gc-reg whitespace-nowrap">
                              {formatDate(day.date)}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Agent Cost Breakdown Section */}
          <Card className="border-border">
            <CardContent className="p-6">
              <div className="space-y-4">
                {/* Header */}
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-maitai-green/10 rounded-lg">
                    <BarChart3Icon className="w-5 h-5 text-maitai-green" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-gc-med text-foreground mb-1">
                      Agent Cost
                    </h3>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-gc-demi-bold text-foreground">
                        {totalSpend.toLocaleString()} credits
                      </span>
                      <span className="text-sm text-muted-foreground font-gc-reg">
                        • {formatNumber(totalRequests)} requests
                      </span>
                    </div>
                  </div>
                </div>

                {/* Cost chart */}
                <div className="h-32 flex items-end gap-1 bg-background/50 rounded px-2 pt-2">
                  {usageData.length === 0 ? (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground font-gc-reg text-sm">
                      No cost data available
                    </div>
                  ) : (
                    usageData.map((day) => {
                      const heightPercent = (day.cost / maxCost) * 100;

                      return (
                        <div
                          key={`cost-${day.date}`}
                          className="flex-1 flex flex-col items-center group relative min-w-[3px]">
                          {/* Bar */}
                          <div
                            className="w-full bg-maitai-green hover:bg-maitai-green/80 transition-colors rounded-t cursor-pointer"
                            style={{
                              height: `${Math.max(heightPercent, 3)}%`,
                              minHeight: day.cost > 0 ? "6px" : "2px",
                            }}
                          />

                          {/* Tooltip */}
                          <div className="absolute bottom-full mb-2 hidden group-hover:block bg-popover border border-border rounded px-2 py-1 text-xs font-gc-reg whitespace-nowrap z-10 shadow-lg">
                            <div className="font-gc-med">
                              {formatDate(day.date)}
                            </div>
                            <div className="text-muted-foreground">
                              {day.cost.toLocaleString()} credits • {day.requests} requests
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Date range */}
                {usageData.length > 0 && (
                  <div className="flex justify-between text-[10px] text-muted-foreground font-gc-reg px-2">
                    <span>{formatDate(usageData[0].date)}</span>
                    <span>
                      {formatDate(usageData[usageData.length - 1].date)}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default AgentUsage;
